// src/components/RPA/buttons/AnalyzeRegulatoryButton.js
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const api = process.env.REACT_APP_API;

const POLL_MS = 8000;        // Poll every 8 seconds (regulatory analysis takes longer)
const MAX_MINUTES = 25;      // Stop polling after 25 minutes

const AnalyzeRegulatoryButton = ({ docId, disabledHint }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  // analysis result and/or status
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  // progress & status
  const [phase, setPhase] = useState('idle');      // idle | queued | extracting | analyzing | done | error
  const [queuedMsg, setQueuedMsg] = useState('');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const timerRef = useRef(null);
  const tickRef = useRef(null);

  // --- helpers --------------------------------------------------------------

  const stopTimers = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const closePanel = () => {
    stopTimers();
    setOpen(false);
  };

  // Poll /admin/rpa/docs for this docId, return doc or null
  const fetchDocMeta = async () => {
    const res = await axios.get(`${api}/admin/rpa/docs`, { withCredentials: true });
    const docs = Array.isArray(res?.data?.items) ? res.data.items : [];
    return docs.find(d => String(d._id) === String(docId)) || null;
  };

  // Polling loop when queued
  const beginPolling = () => {
    // elapsed timer
    tickRef.current = setInterval(() => {
      setElapsedSec(sec => sec + 1);
    }, 1000);

    // status poller
    timerRef.current = setInterval(async () => {
      try {
        const d = await fetchDocMeta();
        if (!d) return;

        // If regulatoryFindings present, we are done
        if (d.regulatoryFindings) {
          stopTimers();
          setPhase('done');
          setResult({
            success: true,
            analysis: { 
              azure: true, 
              documentIntelligence: true,
              findings: d.regulatoryFindings 
            },
          });
          return;
        }

        // Still queued/working — update the UI hints
        if (d.regulatoryAnalysisQueuedAt) {
          setPhase('analyzing');
          setLastUpdatedAt(d.updatedAt || d.createdAt || null);
        }

        // Safety stop after MAX_MINUTES
        if (elapsedSec >= MAX_MINUTES * 60) {
          stopTimers();
          setPhase('error');
          setErr('Regulatory analysis timed out. This may indicate the document is too complex or contains no extractable compliance rules.');
        }
      } catch (e) {
        // transient errors — keep polling unless it persists too long
        console.warn('[RPA] regulatory status poll failed:', e?.message || e);
      }
    }, POLL_MS);
  };

  const run = async () => {
    if (!docId) return;
    setBusy(true);
    setErr('');
    setResult(null);
    setElapsedSec(0);
    setLastUpdatedAt(null);
    stopTimers();

    try {
      // kick off regulatory analysis
      const res = await axios.post(
        `${api}/admin/rpa/docs/${docId}/analyze/regulatory`,
        {},
        { withCredentials: true, validateStatus: () => true }
      );

      // 200 => analysis is already available (cached) or completed inline
      if (res.status === 200) {
        setResult(res.data || { message: 'No data returned' });
        setPhase('done');
        setOpen(true);
        return;
      }

      // 202 => queued; start polling
      if (res.status === 202) {
        setPhase('queued');
        setQueuedMsg(res.data?.message || 'Queued for regulatory compliance analysis...');
        setOpen(true);
        beginPolling();
        return;
      }

      // other => error
      console.error('[RPA] analyze regulatory unexpected:', res.status, res.data);
      setErr(res.data?.message || 'Regulatory analysis failed.');
      setPhase('error');
      setOpen(true);
    } catch (e) {
      console.error('[RPA] analyze regulatory failed:', e);
      setErr('Regulatory analysis failed.');
      setPhase('error');
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  // Clean up timers if component unmounts or panel closes
  useEffect(() => {
    return () => stopTimers();
  }, []);

  // --- render helpers -------------------------------------------------------

  const renderSourceLabel = () => {
    if (!result) return null;

    // If Azure Document Intelligence + AI responded
    if (result.analysis?.documentIntelligence) {
      return (
        <p style={{ color: 'green', fontWeight: 'bold', marginBottom: 6 }}>
          ✅ Azure Document Intelligence + AI Analysis
        </p>
      );
    }

    // Otherwise fallback heuristic
    return (
      <p style={{ color: '#666', fontStyle: 'italic', marginBottom: 6 }}>
        ⚠️ Heuristic Analysis (Fallback)
      </p>
    );
  };

  const renderComplianceRules = () => {
    if (!result?.analysis?.findings) return null;

    let findings;
    try {
      findings = typeof result.analysis.findings === 'string' 
        ? JSON.parse(result.analysis.findings) 
        : result.analysis.findings;
    } catch {
      return <p style={{ color: '#666' }}>Unable to parse compliance findings</p>;
    }

    const rules = findings.compliance_rules || [];
    const restrictions = findings.fund_restrictions || [];

    return (
      <div style={findingsContainer}>
        {rules.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
              Compliance Rules ({rules.length})
            </h4>
            <div style={rulesGrid}>
              {rules.slice(0, 8).map((rule, idx) => (
                <div key={idx} style={ruleCard}>
                  <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>
                    {rule.rule_id || `Rule ${idx + 1}`}
                  </div>
                  <div style={{ fontSize: 11, color: '#4b5563', marginBottom: 4 }}>
                    {rule.requirement}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <span style={{ ...badge, background: getSeverityColor(rule.violation_severity) }}>
                      {rule.violation_severity || 'medium'}
                    </span>
                    <span style={{ ...badge, background: '#f3f4f6', color: '#6b7280' }}>
                      {rule.validation_type || 'validation'}
                    </span>
                  </div>
                </div>
              ))}
              {rules.length > 8 && (
                <div style={{ ...ruleCard, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    +{rules.length - 8} more rules...
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {restrictions.length > 0 && (
          <div>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
              Fund Restrictions ({restrictions.length})
            </h4>
            <div style={restrictionsContainer}>
              {restrictions.slice(0, 5).map((restriction, idx) => (
                <div key={idx} style={restrictionItem}>
                  <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 2 }}>
                    {restriction.restriction_type?.replace('_', ' ').toUpperCase() || 'RESTRICTION'}
                  </div>
                  <div style={{ fontSize: 11, color: '#4b5563' }}>
                    {restriction.description}
                  </div>
                  {restriction.applies_to && restriction.applies_to !== 'all_funds' && (
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                      Applies to: {restriction.applies_to}
                    </div>
                  )}
                </div>
              ))}
              {restrictions.length > 5 && (
                <div style={{ fontSize: 11, color: '#6b7280', padding: 8 }}>
                  +{restrictions.length - 5} more restrictions...
                </div>
              )}
            </div>
          </div>
        )}

        {rules.length === 0 && restrictions.length === 0 && (
          <p style={{ color: '#6b7280', fontSize: 12 }}>
            No specific compliance rules or fund restrictions were extracted from this document.
          </p>
        )}
      </div>
    );
  };

  const renderStatus = () => {
    if (phase === 'queued') {
      return (
        <div style={statusBox}>
          <strong>Queued for Analysis</strong>
          <div style={{ fontSize: 13, color: '#555' }}>{queuedMsg}</div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner />
            <span style={{ fontSize: 12, color: '#777' }}>
              Waiting for Document Intelligence... {formatElapsed(elapsedSec)}
            </span>
          </div>
        </div>
      );
    }

    if (phase === 'analyzing') {
      return (
        <div style={statusBox}>
          <strong>Extracting Compliance Rules...</strong>
          <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
            Processing regulatory sections with Azure AI
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner />
            <span style={{ fontSize: 12, color: '#777' }}>
              This can take several minutes. Elapsed {formatElapsed(elapsedSec)}
              {lastUpdatedAt ? ` • Last update ${new Date(lastUpdatedAt).toLocaleTimeString()}` : ''}
            </span>
          </div>
          <ProgressBar />
        </div>
      );
    }

    if (phase === 'error') {
      return <p style={{ color: 'crimson' }}>{err || 'Something went wrong.'}</p>;
    }

    return null;
  };

  return (
    <div>
      <button
        onClick={run}
        disabled={busy || !!disabledHint}
        title={disabledHint ? 'Regulatory analysis requires PDF documents' : 'Extract compliance rules from federal regulatory documents'}
        style={{ 
          background: '#6366f1', 
          color: 'white', 
          border: 'none', 
          padding: '4px 8px', 
          borderRadius: 4,
          fontSize: 12,
          cursor: disabledHint ? 'not-allowed' : 'pointer',
          opacity: disabledHint ? 0.6 : 1
        }}
      >
        {busy ? 'Analyzing…' : 'Extract Rules'}
      </button>

      {open && (
        <div style={panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong>Regulatory Compliance Analysis</strong>
            <button onClick={closePanel} style={{ padding: '2px 8px' }}>Close</button>
          </div>

          {renderStatus()}

          {/* Show the final result once ready */}
          {phase === 'done' && !err && result && (
            <>
              {renderSourceLabel()}
              {renderComplianceRules()}
              
              {/* Raw JSON for debugging */}
              <details style={{ marginTop: 12 }}>
                <summary style={{ fontSize: 12, color: '#666', cursor: 'pointer' }}>
                  Raw Analysis (Debug)
                </summary>
                <pre style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0 0', fontSize: 10, background: '#f8f9fa', padding: 8, borderRadius: 4, maxHeight: 200, overflow: 'auto' }}>
                  {typeof result.analysis.findings === 'string' 
                    ? result.analysis.findings 
                    : JSON.stringify(result.analysis.findings, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------- helpers & styles ---------------------------- */

function getSeverityColor(severity) {
  switch (severity) {
    case 'critical': return '#fef2f2';
    case 'high': return '#fef3c7';
    case 'medium': return '#eff6ff';
    case 'low': return '#f0fdf4';
    default: return '#f3f4f6';
  }
}

function formatElapsed(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const panel = {
  marginTop: 8,
  background: '#f8f9fb',
  border: '1px solid #e2e5ea',
  padding: 12,
  borderRadius: 8,
  maxWidth: 700,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
};

const statusBox = {
  background: '#fff',
  border: '1px solid #e6e9ef',
  padding: 10,
  borderRadius: 8,
  marginBottom: 10
};

const findingsContainer = {
  background: '#fff',
  border: '1px solid #e6e9ef',
  padding: 12,
  borderRadius: 8,
  marginBottom: 8
};

const rulesGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 8
};

const ruleCard = {
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  padding: 8,
  borderRadius: 6,
  minHeight: 80
};

const restrictionsContainer = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6
};

const restrictionItem = {
  background: '#fef3c7',
  border: '1px solid #fbbf24',
  padding: 8,
  borderRadius: 6
};

const badge = {
  fontSize: 9,
  padding: '2px 6px',
  borderRadius: 12,
  fontWeight: 'bold',
  textTransform: 'uppercase'
};

const Spinner = () => (
  <span
    style={{
      width: 14,
      height: 14,
      border: '2px solid #cbd3e1',
      borderTop: '2px solid #6366f1',
      borderRadius: '50%',
      display: 'inline-block',
      animation: 'spin 1s linear infinite'
    }}
  />
);

const ProgressBar = () => (
  <div style={{ marginTop: 8, height: 8, background: '#e9edf5', borderRadius: 6, overflow: 'hidden' }}>
    <div style={{
      width: '40%',
      height: '100%',
      background: '#6366f1',
      animation: 'bar 2s ease-in-out infinite'
    }} />
    <style>{`
      @keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }
      @keyframes bar {
        0% { transform: translateX(-100%); }
        50% { transform: translateX(50%); }
        100% { transform: translateX(150%); }
      }
    `}</style>
  </div>
);

export default AnalyzeRegulatoryButton;